import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Button, buttonVariants } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { CalendarClock, FileText, LoaderCircle, Trash2, X } from 'lucide-react'
import { router } from '@inertiajs/react'
import { useState } from 'react'
import type { MemoryCopy, MemoryDocumentDetail, MemoryDocumentSummary, MemoryRecallResult, MemoryRow } from './types'
import { displayDocumentName, formatMemoryDate } from './utils'

interface Props {
  workerId: string
  copy: MemoryCopy
  summary: MemoryDocumentSummary
  detail: MemoryDocumentDetail | null
  loading: boolean
  error: string | null
  canManage: boolean
  onSelectMemory: (row: MemoryRow) => void
  onDeleted: () => void
  onClose: () => void
}

export default function MemoryDocumentPanel({ workerId, copy, summary, detail, loading, error, canManage, onSelectMemory, onDeleted, onClose }: Props) {
  const [confirming, setConfirming] = useState(false)
  const [processing, setProcessing] = useState(false)
  const document = detail || summary
  const isMarkdown = /\.(?:md|markdown)$/i.test(document.name)
  const eventDate = detail?.event_date
  const memoryCount = document.memory_unit_count

  const remove = () => {
    setProcessing(true)
    router.delete(`/dashboard/digital_workers/${workerId}/memory_document`, {
      data: { document_id: summary.id },
      preserveScroll: true,
      onSuccess: onDeleted,
      onFinish: () => setProcessing(false),
    })
  }

  const selectMemory = (memory: MemoryRecallResult) => {
    onSelectMemory({ ...memory, fact_type: memory.type || memory.fact_type, entities: memory.entities?.join(', ') || null })
  }

  return (
    <article className="flex h-full flex-col overflow-hidden">
      <header className="flex items-center gap-3 border-b border-border px-5 py-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground"><FileText className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-faint-foreground">{copy.documents.detail_title}</p>
          <h3 className="mt-0.5 truncate text-base font-semibold text-foreground">{displayDocumentName(document.name)}</h3>
        </div>
        <Button type="button" variant="ghost" size="icon-sm" onClick={onClose} aria-label={copy.detail.close}><X /></Button>
      </header>

      {/* Sem coluna lateral: o conteúdo da fonte ocupa a largura inteira e os
          poucos metadados que sobraram (contexto e a data relacionada — não a
          de criação) viram uma linha discreta. As contagens por tipo saíram
          junto com os tipos; o total agora vive no cabeçalho das memórias
          extraídas, lá embaixo. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {loading && <p className="flex min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground"><LoaderCircle className="h-4 w-4 animate-spin text-primary" />{copy.documents.loading}</p>}
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        {detail && (
          <section className="min-w-0 space-y-3">
            <h4 className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-faint-foreground">{copy.documents.source_text}</h4>
            <ScrollArea aria-label={copy.documents.source_text} className="h-[min(22rem,42vh)] rounded-lg border border-border bg-background">
              <div className="p-4 pr-5 text-sm leading-6 text-foreground/85">
                {detail.original_text ? (isMarkdown ? <SimpleMarkdown content={detail.original_text} allowImages={false} /> : <p className="whitespace-pre-wrap">{detail.original_text}</p>) : <p className="text-muted-foreground">{copy.documents.preview_unavailable}</p>}
              </div>
            </ScrollArea>

            {/* O `context` do documento é metadado de ingestão (caminho do
                transcript, line=, role=, seen_at=) — plumbing, não conteúdo.
                Fica fora da tela do cliente. */}
            {eventDate && (
              <p className="flex items-center gap-2 text-[11px] text-faint-foreground">
                <CalendarClock className="h-3.5 w-3.5" />
                {copy.documents.event_date} · {formatMemoryDate(eventDate)}
              </p>
            )}
          </section>
        )}

        {/* O total vem da contagem do documento (que já existe no resumo, sem
            depender do detalhe) e a lista de prévia vem de uma busca limitada à
            parte. Então count > 0 com lista vazia é estado real — inclusive
            quando o detalhe falha em carregar. A seção aparece sempre que
            houver memórias; só a prévia depende do detalhe, senão a fonte
            pareceria não ter gerado nada. */}
        {!loading && memoryCount > 0 && (
          <section className="mt-6 border-t border-border pt-5">
            <div className="flex items-baseline justify-between gap-3">
              <h4 className="text-xs font-semibold text-foreground">{copy.documents.linked_memories}</h4>
              <span className="shrink-0 text-[11px] tabular-nums text-faint-foreground">{memoryCount}</span>
            </div>
            <p className="mt-1 text-[10.5px] text-faint-foreground">{copy.documents.linked_memories_description}</p>
            {detail && detail.linked_memories.length > 0 ? (
              <div className="mt-3 grid auto-rows-fr gap-2 md:grid-cols-2">
                {detail.linked_memories.map((memory) => <LinkedMemory key={memory.id} memory={memory} onSelect={selectMemory} />)}
              </div>
            ) : (
              <p className="mt-3 rounded-lg bg-muted/40 px-3 py-2.5 text-xs leading-5 text-muted-foreground">
                {copy.documents.linked_memories_unavailable}
              </p>
            )}
          </section>
        )}

      </div>

      {/* Excluir a fonte apaga as memórias extraídas dela: ação séria, mas não
          é o assunto do modal — rodapé, tom neutro, vermelho só no hover. */}
      {canManage && (
        <footer className="flex justify-start border-t border-border px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setConfirming(true)}
            className="gap-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="size-3.5" />
            {copy.documents.delete}
          </Button>
        </footer>
      )}

      <AlertDialog open={confirming} onOpenChange={(open) => { if (!open && !processing) setConfirming(false) }}>
        <AlertDialogContent className="z-[80]">
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.documents.delete_title}</AlertDialogTitle>
            <AlertDialogDescription>{copy.documents.delete_description}</AlertDialogDescription>
          </AlertDialogHeader>
          <p className="text-sm font-medium text-destructive">{copy.documents.extracted_memories.replace('%{count}', String(memoryCount))}</p>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>{copy.documents.cancel}</AlertDialogCancel>
            <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} disabled={processing} onClick={(event) => { event.preventDefault(); remove() }}>
              {processing && <LoaderCircle className="animate-spin" />}{processing ? copy.documents.deleting : copy.documents.delete_confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  )
}

function LinkedMemory({ memory, onSelect }: { memory: MemoryRecallResult; onSelect: (memory: MemoryRecallResult) => void }) {
  const date = memory.mentioned_at || memory.occurred_start || memory.created_at
  return (
    <button type="button" onClick={() => onSelect(memory)} className="flex h-full min-h-24 flex-col rounded-lg border border-border bg-background p-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
      {date && <span className="mb-1 block text-[10.5px] tabular-nums text-faint-foreground">{formatMemoryDate(date, false)}</span>}
      <span className="line-clamp-4 block text-xs leading-5 text-foreground/85">{memory.text}</span>
    </button>
  )
}
